import { useMemo } from 'react';
import differenceInMinutes from 'date-fns/differenceInMinutes';

// Components
import MatchContainer from '../../components/MatchContainer';
import {
  ConfigContext,
  EventInfo as SingleEventInfo,
  FansUnitedFullTimeOutcome,
  GameTeam,
  OddsDisplay,
  Round,
  SMPContainer,
  SMPFlex,
  SMPLoader,
  SMPText,
  Tournament,
} from '@velinsi/smp-widgets-ui';

// Hooks
import useReconcileScopeMarkets from '../../hooks/useReconcileScopeMarkets';
import useSingleEvent from '../../hooks/useSingleEvent';
import { useIsSmartphoneDevice } from '../../../../../packages/hooks/useIsSmartphoneDevice';

// Utils
import {
  constructAdditionalEventStatusInfo,
  defaultHandleEventClick,
  defaultHandleTeamClick,
  defaultHandleTournamentClick,
  extractOnlyDateSDKOptions,
} from '@sportal365/smp-widget-utils';

// Types
import { BaseWidgetProps, FansUnitedConfig, IBaseTeam, ISingleEventConfig, ITournament } from '../../../../../packages/types';

// Misc
import { normalizeFootballGameData } from '@sportal365/smp-widget-normalizers';
import { Sports } from '../../../../../packages/constants/sportType';
import ThemeProvider from '../../../../../packages/theme/ThemeProvider';
import { IN_PROGRESS, LIVE } from '../../../../../packages/constants/statusTypes';
import { BASE_VARIANT, LIVE_VARIANT, PRIMARY_GRAY_VARIANT } from '../../../../../packages/constants/statusTextVariants';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import { LinkWrapper } from '../../../../../packages/HOC/LinkWrapper';
import useOverlapOdds from '../../../../../packages/hooks/useOverlapOdds';
import { MainEvents } from '../../components/MainEvents';

export const SingleEventWidget = ({
  themes,
  config,
  subscribers,
  shadow,
  getAuthToken,
  signOut,
  sdkOptions,
  cuid,
}: BaseWidgetProps<ISingleEventConfig & FansUnitedConfig>) => {
  const { ref } = useRootNodeRef();
  const { oddsMarket, oddsMarketScope } = useReconcileScopeMarkets(config?.dataOddsMarket);
  const { areBettingOddsOverlapVisible } = useOverlapOdds({ dataBettingLogoOverlap: config?.dataBettingLogoOverlap });
  const displaySingleEventMainEvents = config?.dataSingleEventMainEventsDisplay;

  const {
    showWidget,
    handleClick,
    widgetData: { footballSingleEvent, odds, isLoading },
  } = useSingleEvent({
    subscribers,
    dataWidgetId: config?.dataWidgetId,
    dataWidgetSport: config?.dataWidgetSport,
    dataRefreshTime: config?.dataRefreshTime,
    dataMatchId: config?.dataMatchId,
    dataOddsDisplay: config?.dataOddsDisplay,
    dataOddsBettingId: config?.dataOddsBettingId,
    dataOddsPreEventOnly: config?.dataOddsPreEventOnly,
    dataOddsMarketValueType: config?.dataOddsMarketValueType,
    dataOddsMarket: oddsMarket,
    dataSingleEventMainEventsDisplay: displaySingleEventMainEvents,
    dataWidgetCuid: cuid,
  });

  const { isMobile } = useIsSmartphoneDevice(ref);

  const showFansUnited = useMemo(() => {
    const dateNow = new Date();
    const kickOff = footballSingleEvent?.kickoffTime ? new Date(footballSingleEvent.kickoffTime) : new Date();
    const diffInMins = differenceInMinutes(dateNow, kickOff);
    const configDiff = -(config?.fansUnitedHideBeforeGameStart || 15);
    const showFrom = config?.fansUnitedShowFrom ? new Date(config?.fansUnitedShowFrom) : new Date();

    return configDiff >= diffInMins && config?.fansUnitedEnabled && dateNow >= showFrom;
  }, [footballSingleEvent, config]);

  if (!showWidget) return null;

  const match = normalizeFootballGameData({
    game: footballSingleEvent,
    dataEntityLinks: config?.dataEntityLinks,
    dataShortStatusType: config?.dataShortStatusType,
    dataTeamNameType: config?.dataTeamNameType,
    sdkDateOptions: extractOnlyDateSDKOptions(sdkOptions),
  });

  const handleTeamClick = ({ id, name }: IBaseTeam) => defaultHandleTeamClick(handleClick, { id, name });

  const handleEventClick = (event: string | number) => defaultHandleEventClick(handleClick, Number(event));

  const handleTournamentClick = (tournament: ITournament) => defaultHandleTournamentClick(handleClick, tournament.id, tournament.name);

  if (isLoading) return <SMPLoader size={3} thickness={4} color='spinner' loaderMinHeight='300px' />;

  const statusTextVariant = match?.matchStatus?.type === LIVE ? LIVE_VARIANT : PRIMARY_GRAY_VARIANT;
  const applyStyleForMainEvents =
    displaySingleEventMainEvents && match?.mainEvents && match?.mainEvents.length > 0 ? { padding: '16px 8px' } : {};

  return (
    <ConfigContext.Provider value={{ ...config, sdkOptions }}>
      <ThemeProvider isLoading={isLoading} shadow={shadow} themes={themes} dataTheme={config?.dataTheme}>
        <LinkWrapper
          url={match?.matchLink}
          target={match?.urlTarget}
          shouldWrap={config?.dataWrapperLink}
          onClick={() => match?.id && handleEventClick(match.id)}
        >
          <SMPContainer ref={ref} size='4' variant='footballSingleEventContainer'>
            {match && (
              <MatchContainer
                style={{ ...applyStyleForMainEvents }}
                variant={footballSingleEvent?.status?.type === IN_PROGRESS ? LIVE_VARIANT : BASE_VARIANT}
                type='singleEvent'
              >
                {config?.dataAdditionalInfo && (
                  <>
                    <SMPFlex css={{ pl: '$3' }}>
                      {match.tournament && (
                        <Tournament
                          sport={Sports.FOOTBALL}
                          tournament={match.tournament}
                          handleTournamentClick={handleTournamentClick}
                          hideLogo
                          hideStandingsIcon
                          hideSubName
                          variant='footballSingleEventTournamentContainer'
                        />
                      )}
                    </SMPFlex>
                    <SMPFlex css={{ pl: '$3', pb: match.round ? '' : '$3' }}>
                      {match.round && (
                        <Round
                          round={match?.round}
                          matchDate={match.kickOffTime}
                          dataRoundLabel={config?.dataLabels?.round}
                          sdkOptions={extractOnlyDateSDKOptions(sdkOptions)}
                        />
                      )}
                    </SMPFlex>
                  </>
                )}
                <SMPFlex variant='eventInfo' css={{ pl: '$3' }}>
                  <GameTeam
                    isMobile={isMobile}
                    sportEntityVariant={'homeTeam'}
                    handleTeamClick={handleTeamClick}
                    team={match?.homeTeam}
                    teamLink={match?.homeTeam.url}
                    teamName={match?.homeTeam?.name}
                    teamLogo={match?.homeTeam.logo}
                    urlTarget={match?.urlTarget}
                  />

                  <SingleEventInfo
                    handleEventClick={handleEventClick}
                    match={match}
                    matchLink={match.matchLink}
                    dataShortStatusType={config?.dataShortStatusType}
                    displayDate={!config?.dataAdditionalInfo}
                  />

                  <GameTeam
                    isMobile={isMobile}
                    sportEntityVariant={'awayTeam'}
                    handleTeamClick={handleTeamClick}
                    team={match?.awayTeam}
                    teamLink={match?.awayTeam.url}
                    teamName={match?.awayTeam?.name}
                    teamLogo={match?.awayTeam.logo}
                    urlTarget={match?.urlTarget}
                  />
                </SMPFlex>
                <SMPFlex css={{ pl: '$3' }}>
                  <SMPText css={{ mt: '$2' }} variant={statusTextVariant} size='2'>
                    {constructAdditionalEventStatusInfo(match, config?.dataLabels)}
                  </SMPText>
                </SMPFlex>
              </MatchContainer>
            )}
            {displaySingleEventMainEvents && match?.mainEvents && match?.mainEvents.length > 0 && (
              <MainEvents
                mainEvents={match?.mainEvents ?? []}
                dataLabels={config?.dataLabels}
                oddsAvailable={config?.dataOddsDisplay && odds && odds.length > 0}
              />
            )}
          </SMPContainer>
        </LinkWrapper>

        {config?.dataOddsDisplay && odds && match?.id && (
          <OddsDisplay
            odds={odds}
            handleClick={handleClick}
            marketSelectionDisplay={config.dataOddsMarket === undefined}
            isHeader
            isStandalone
            oddsMarketScope={oddsMarketScope}
            areBettingOddsOverlapVisible={areBettingOddsOverlapVisible}
          />
        )}

        {showFansUnited && (
          <FansUnitedFullTimeOutcome
            labels={config?.dataLabels}
            matchId={match?.id}
            subscribers={subscribers}
            homeTeam={match?.homeTeam.name}
            awayTeam={match?.awayTeam.name}
            fansUnitedApiKey={config?.fansUnitedApiKey}
            fansUnitedClientId={config?.fansUnitedClientId}
            fansUnitedEnvironment={config?.fansUnitedEnvironment}
            fansUnitedIdSchema={config?.fansUnitedIdSchema}
            fansUnitedExpanded={config?.fansUnitedExpanded}
            fansUnitedGetAuthToken={getAuthToken}
            fansUnitedSignOut={signOut}
          />
        )}
      </ThemeProvider>
    </ConfigContext.Provider>
  );
};
